// Embedded image links for the image block.
//
// An image block normally points at a file (uploaded or on a server path) and
// renders as an <img>. Some image sources — Pinterest, Flickr, Instagram and
// friends — only allow their images to be reused through a provider-hosted
// iframe. Those blocks stay `type: 'image'` so they keep every image feature
// (APA citation fields, the reference-list collector, detaching the citation
// into its own movable object in the slide designer); they simply carry
// `imageEmbedSrc` instead of `src` and render an iframe.
//
// Block fields written by this module's consumers:
//   imageEmbedSrc      iframe src (what actually renders)
//   imageEmbedUrl      canonical page URL for the image (used for the citation)
//   imageEmbedProvider display name of the host, e.g. "Pinterest"
//   imageEmbedWidth    provider's suggested width in px
//   imageEmbedHeight   provider's suggested height in px

const DEFAULT_EMBED_SIZE = { width: 345, height: 359 };

const hostMatches = (host, domain) => host === domain || host.endsWith(`.${domain}`);

const safeUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  try {
    const url = new URL(value.trim());
    // Only https embeds: an http iframe is blocked as mixed content once the
    // lesson is exported and opened from an https LMS.
    if (url.protocol !== 'https:') return null;
    return url;
  } catch {
    return null;
  }
};

// Provider registry. `fromPage` turns a normal page URL into an embed src;
// `fromEmbed` turns an embed src back into the page URL so the citation can
// link to something a reader can actually open.
const PROVIDERS = [
  {
    key: 'pinterest',
    name: 'Pinterest',
    size: { width: 345, height: 359 },
    matchesPage: (host) => hostMatches(host, 'pinterest.com') || hostMatches(host, 'pinterest.ca')
      || hostMatches(host, 'pinterest.co.uk') || hostMatches(host, 'pin.it'),
    matchesEmbed: (host) => hostMatches(host, 'pinterest.com') || hostMatches(host, 'assets.pinterest.com'),
    fromPage: (url) => {
      const pin = url.pathname.match(/\/pin\/([^/?#]+)/i);
      if (pin) return `https://assets.pinterest.com/ext/embed.html?id=${encodeURIComponent(pin[1])}`;
      // Boards and profiles embed by URL rather than by id.
      const board = url.pathname.replace(/^\/+|\/+$/g, '');
      if (!board) return null;
      const type = board.split('/').length >= 2 ? 'board' : 'user';
      return `https://assets.pinterest.com/ext/embed.html?type=${type}&url=${encodeURIComponent(url.href)}`;
    },
    fromEmbed: (url) => {
      const id = url.searchParams.get('id');
      if (id) return `https://www.pinterest.com/pin/${id}/`;
      const target = url.searchParams.get('url');
      return safeUrl(target) ? target : null;
    }
  },
  {
    key: 'instagram',
    name: 'Instagram',
    size: { width: 400, height: 600 },
    matchesPage: (host) => hostMatches(host, 'instagram.com'),
    matchesEmbed: (host) => hostMatches(host, 'instagram.com'),
    fromPage: (url) => {
      const post = url.pathname.match(/\/(p|reel|tv)\/([^/?#]+)/i);
      if (!post) return null;
      return `https://www.instagram.com/${post[1]}/${post[2]}/embed/`;
    },
    fromEmbed: (url) => {
      if (!/\/embed\/?$/i.test(url.pathname)) return null;
      const post = url.pathname.match(/\/(p|reel|tv)\/([^/?#]+)/i);
      return post ? `https://www.instagram.com/${post[1]}/${post[2]}/` : null;
    }
  },
  {
    key: 'flickr',
    name: 'Flickr',
    size: { width: 500, height: 375 },
    matchesPage: (host) => hostMatches(host, 'flickr.com') || hostMatches(host, 'flic.kr'),
    matchesEmbed: (host) => hostMatches(host, 'flickr.com') || hostMatches(host, 'embedr.flickr.com'),
    fromPage: (url) => {
      const photo = url.pathname.match(/\/photos\/([^/?#]+)\/(\d+)/i);
      if (!photo) return null;
      return `https://www.flickr.com/photos/${photo[1]}/${photo[2]}/player/`;
    },
    fromEmbed: (url) => {
      const isEmbedPath = /\/player\/?$/i.test(url.pathname)
        || hostMatches(url.hostname.toLowerCase(), 'embedr.flickr.com');
      if (!isEmbedPath) return null;
      const photo = url.pathname.match(/\/photos\/([^/?#]+)\/(\d+)/i);
      return photo ? `https://www.flickr.com/photos/${photo[1]}/${photo[2]}/` : null;
    }
  },
  {
    key: 'imgur',
    name: 'Imgur',
    size: { width: 540, height: 500 },
    matchesPage: (host) => hostMatches(host, 'imgur.com'),
    matchesEmbed: (host) => hostMatches(host, 'imgur.com'),
    fromPage: (url) => {
      const album = url.pathname.match(/\/(a|gallery)\/([^/?#]+)/i);
      if (album) return `https://imgur.com/a/${album[2]}/embed`;
      const single = url.pathname.match(/^\/([A-Za-z0-9]+)$/);
      return single ? `https://imgur.com/${single[1]}/embed` : null;
    },
    fromEmbed: (url) => (/\/embed\/?$/i.test(url.pathname)
      ? `https://imgur.com${url.pathname.replace(/\/embed\/?$/i, '')}`
      : null)
  },
  {
    key: 'giphy',
    name: 'GIPHY',
    size: { width: 480, height: 400 },
    matchesPage: (host) => hostMatches(host, 'giphy.com'),
    matchesEmbed: (host) => hostMatches(host, 'giphy.com'),
    fromPage: (url) => {
      const gif = url.pathname.match(/\/(gifs|clips|stickers)\/([^/?#]+)/i);
      if (!gif) return null;
      // Giphy slugs end with the id: "funny-cat-xyz123" → "xyz123".
      const id = gif[2].split('-').pop();
      return id ? `https://giphy.com/embed/${id}` : null;
    },
    fromEmbed: (url) => {
      const id = url.pathname.match(/\/embed\/([^/?#]+)/i);
      return id ? `https://giphy.com/gifs/${id[1]}` : null;
    }
  }
];

const findProvider = (host, kind) => PROVIDERS.find((provider) => (
  kind === 'embed' ? provider.matchesEmbed(host) : provider.matchesPage(host)
)) || null;

// Turn a bare hostname into something readable for the citation's Source field
// ("images.example.com" → "Example").
const hostToProviderName = (host) => {
  const parts = String(host || '').replace(/^www\./i, '').split('.');
  const label = parts.length > 1 ? parts[parts.length - 2] : parts[0];
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const readDimension = (value) => {
  const number = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(number) && number > 0 ? number : null;
};

// Pull the src/width/height out of a pasted <iframe …> snippet. Attribute
// order and quote style vary between providers, so each is matched on its own.
export const extractIframeAttributes = (raw) => {
  if (!raw || typeof raw !== 'string' || !/<iframe/i.test(raw)) return null;
  const attribute = (name) => {
    const match = raw.match(new RegExp(`${name}\\s*=\\s*["']([^"']*)["']`, 'i'));
    return match ? match[1].trim() : '';
  };
  const src = attribute('src');
  if (!src) return null;
  return {
    src,
    width: readDimension(attribute('width')),
    height: readDimension(attribute('height'))
  };
};

/**
 * Parse whatever the user pasted — a full <iframe> snippet, a provider embed
 * URL, or an ordinary page URL — into the fields an embedded image block needs.
 *
 * @returns {{embedSrc, pageUrl, provider, providerKey, width, height}|null}
 *          null when the input is empty, not https, or not a URL at all.
 */
export const parseImageEmbed = (raw) => {
  const input = typeof raw === 'string' ? raw.trim() : '';
  if (!input) return null;

  const iframe = extractIframeAttributes(input);
  const candidate = safeUrl(iframe ? iframe.src : input);
  if (!candidate) return null;

  const host = candidate.hostname.toLowerCase();
  const asEmbed = findProvider(host, 'embed');
  const asPage = findProvider(host, 'page');

  // An iframe snippet is already an embed src; a bare URL might be either.
  let provider = null;
  let embedSrc = '';
  let pageUrl = '';

  const looksLikeEmbed = Boolean(iframe) || (asEmbed && asEmbed.fromEmbed(candidate));

  if (looksLikeEmbed && asEmbed) {
    provider = asEmbed;
    embedSrc = candidate.href;
    pageUrl = asEmbed.fromEmbed(candidate) || '';
  } else if (asPage) {
    provider = asPage;
    embedSrc = asPage.fromPage(candidate) || '';
    pageUrl = candidate.href;
    if (!embedSrc) return null;
  } else {
    // Unknown host: trust the pasted iframe src, otherwise treat the URL as the
    // embed target directly. Either way the user can still fill the citation in.
    embedSrc = candidate.href;
    pageUrl = iframe ? '' : candidate.href;
  }

  const size = provider?.size || DEFAULT_EMBED_SIZE;
  return {
    embedSrc,
    pageUrl,
    provider: provider?.name || hostToProviderName(host),
    providerKey: provider?.key || 'generic',
    width: iframe?.width || size.width,
    height: iframe?.height || size.height
  };
};

export const isEmbeddedImage = (block) => Boolean(block && block.imageEmbedSrc);

/**
 * True when an image block has something to show.
 *
 * Every "is this block empty?" check must go through this rather than testing
 * `src` directly: an embedded image deliberately has no `src`, and a plain
 * `Boolean(block.src)` test silently classifies it as an empty block — which
 * drops it from the PowerPoint/PDF export payload and flags its slide as
 * incomplete in the outline.
 */
export const imageBlockHasContent = (block) => Boolean(block && (block.src || block.imageEmbedSrc));

/**
 * Citation fields to prefill from a parsed embed. Only the two facts the link
 * itself proves — where it came from and where it lives — are filled in; title,
 * author and date still come from the metadata fetch or from the user.
 */
export const embedCitationDefaults = (parsed) => {
  if (!parsed) return {};
  return {
    imageSource: parsed.provider || '',
    imageUrl: parsed.pageUrl || parsed.embedSrc || ''
  };
};

/**
 * Frame geometry for rendering. Providers publish a fixed pixel size; the
 * aspect ratio derived from it lets the frame stay responsive on narrow
 * screens instead of overflowing.
 */
export const embedFrameStyle = (block = {}) => {
  const width = readDimension(block.imageEmbedWidth) || DEFAULT_EMBED_SIZE.width;
  const height = readDimension(block.imageEmbedHeight) || DEFAULT_EMBED_SIZE.height;
  return {
    width,
    height,
    aspectRatio: `${width} / ${height}`
  };
};

export const IMAGE_EMBED_DEFAULT_SIZE = DEFAULT_EMBED_SIZE;
